import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import SEO from '../components/SEO';
import '../styles/LegalPages.css';

const PrivacyPolicy = () => {
  // Scroll to top on mount - immediate
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, []);

  return (
    <div className="legal-page">
      <SEO canonical="/privacy-policy" />
      <header className="legal-header">
        <div className="container">
          <Link to="/" className="legal-back-link">
            <ArrowLeft size={18} />
            <span>Back to Home</span>
          </Link>
          <Link to="/" className="legal-logo">
            <img 
              src="https://customer-assets.emergentagent.com/job_accordion-repair/artifacts/06uiebgi_Variant%201_Full%20Logo_Vector%20copy%202.png" 
              alt="All Secured" 
            />
          </Link>
        </div>
      </header>

      <main className="legal-content">
        <div className="container">
          <article className="legal-article">
            <h1>Privacy Policy</h1>
            <p className="legal-subtitle">All Secured Pty Ltd</p>
            <p className="legal-effective-date">Effective Date: 12 February 2026</p>

            <section>
              <h2>1. Who We Are</h2>
              <p>All Secured ("we", "our", "us") provides online cybersecurity education designed to help individuals improve their digital privacy and security practices. If you have any privacy-related questions, you may contact us at:</p>
              <p><strong>Email:</strong> <a href="mailto:allsecured.com.au@proton.me">allsecured.com.au@proton.me</a></p>
            </section>

            <section>
              <h2>2. Information We Collect</h2>
              <p>We collect only the information necessary to operate our services.</p>

              <h3>Information You Provide</h3>
              <p>When you:</p>
              <ul>
                <li>Purchase a course</li>
                <li>Create an account</li>
                <li>Request password recovery</li>
                <li>Contact support</li>
              </ul>

              <p>We may collect:</p>
              <ul>
                <li>Full name (if provided)</li>
                <li>Email address</li>
                <li>Account login credentials (encrypted)</li>
                <li>Course enrollment data</li>
                <li>Support communications</li>
              </ul>

              <h3>Payment Information</h3>
              <p>Payments are processed securely via Stripe. We do not store or have access to your full card details. Stripe may collect:</p>
              <ul>
                <li>Billing name</li>
                <li>Billing address</li>
                <li>Payment method details</li>
                <li>Transaction data</li>
              </ul>
              <p>Stripe's privacy policy applies to payment processing.</p>

              <h3>Automatically Collected Data</h3>
              <p>When you use our website, we may collect:</p>
              <ul>
                <li>IP address</li>
                <li>Browser type</li>
                <li>Device type</li>
                <li>Pages visited</li>
                <li>Referrer information</li>
              </ul>

              <p>This data is used for:</p>
              <ul>
                <li>Security monitoring</li>
                <li>Fraud prevention</li>
                <li>Analytics and performance optimisation</li>
              </ul>
            </section>

            <section>
              <h2>3. How We Use Your Information</h2>
              <p>We use your information to:</p>
              <ul>
                <li>Provide course access</li>
                <li>Process purchases</li>
                <li>Authenticate login sessions</li>
                <li>Send transactional emails (e.g. access links, password reset)</li>
                <li>Improve platform performance</li>
                <li>Prevent fraud and abuse</li>
                <li>Respond to support requests</li>
              </ul>
              <p><strong>We do not sell your personal data.</strong></p>
            </section>

            <section>
              <h2>4. Email Communications</h2>
              <p>We send:</p>
              <ul>
                <li>Course access emails</li>
                <li>Account verification emails</li>
                <li>Password reset emails</li>
                <li>Purchase confirmations</li>
              </ul>
              <p>Payment receipts are sent directly by Stripe. We do not send promotional marketing emails unless you explicitly opt in.</p>
            </section>

            <section>
              <h2>5. Cookies & Tracking</h2>
              <p>We may use:</p>
              <ul>
                <li>Essential cookies (authentication, session security)</li>
                <li>Analytics tools</li>
                <li>Advertising pixels (e.g. Meta Pixel)</li>
              </ul>
              <p>You may disable cookies in your browser settings, though some features may not function correctly.</p>
            </section>

            <section>
              <h2>6. Data Storage & Security</h2>
              <p>We use industry-standard security measures, including:</p>
              <ul>
                <li>Encrypted HTTPS connections</li>
                <li>Secure password hashing</li>
                <li>Token-based authentication</li>
                <li>Limited data retention</li>
                <li>Role-based admin access</li>
              </ul>
              <p>However, no online platform can guarantee absolute security.</p>
            </section>

            <section>
              <h2>7. Data Retention</h2>
              <p>We retain personal information:</p>
              <ul>
                <li>As long as your account is active</li>
                <li>As required for legal or tax obligations</li>
                <li>For fraud prevention purposes</li>
              </ul>
              <p>You may request deletion of your account by contacting us.</p>
            </section>

            <section>
              <h2>8. Your Rights</h2>
              <p>Under applicable Australian privacy law, you may:</p>
              <ul>
                <li>Request access to your personal data</li>
                <li>Request correction of inaccurate data</li>
                <li>Request deletion (subject to legal obligations)</li>
              </ul>
              <p>Contact us at: <a href="mailto:allsecured.com.au@proton.me">allsecured.com.au@proton.me</a></p>
            </section>

            <section>
              <h2>9. Third-Party Services</h2>
              <p>We rely on trusted providers including:</p>
              <ul>
                <li>Stripe (payments)</li>
                <li>Resend (transactional email delivery)</li>
                <li>Vimeo (video hosting)</li>
                <li>Analytics providers</li>
              </ul>
              <p>Each provider maintains its own privacy policy.</p>
            </section>

            <section>
              <h2>10. Updates to This Policy</h2>
              <p>We may update this Privacy Policy periodically. The "Effective Date" at the top reflects the latest revision.</p>
            </section>
          </article>
        </div>
      </main>

      <footer className="legal-footer">
        <div className="container">
          <p>&copy; 2025 All Secured. All rights reserved.</p>
          <p className="legal-footer-stripe">Secure payments powered by Stripe.</p>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
